import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import DocumentSelectionSection from './DocumentSelectionSection';
import ChatSelectionSection from './ChatSelectionSection';
import ProjectDocumentPicker, { useProjectDocumentOptions } from './ProjectDocumentPicker';
import { useRegenerateSelection, RegenerateSelection } from '../hooks/useRegenerateSelection';
import { DroppedDocument } from './DocumentDropZone';

interface WorkflowReportData {
  documents: Array<{
    id: string;
    name: string;
    policyType?: string;
    size?: number;
  }>;
  workflowType: string;
  customerName?: string;
  customerId?: string;
  projectId?: string;
}

interface SelectiveRegenerateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegenerate: (selection: RegenerateSelection) => Promise<void>;
  isRegenerating: boolean;
  workflowData: WorkflowReportData;
  additionalDocuments: DroppedDocument[];
  chatHistory: any[];
}

/**
 * Modal for selective document and chat regeneration
 * Allows users to choose which documents and chat history to include
 */
export const SelectiveRegenerateModal: React.FC<SelectiveRegenerateModalProps> = ({
  isOpen,
  onClose,
  onRegenerate,
  isRegenerating,
  workflowData,
  additionalDocuments,
  chatHistory
}) => {
  // Documents that already live in the customer's projects but aren't part of
  // this report — fetched once while the modal is open.
  const excludeIds = useMemo(
    () => [
      ...workflowData.documents.map(d => d.id),
      ...additionalDocuments.map(d => d.id)
    ],
    [workflowData.documents, additionalDocuments]
  );

  const {
    documents: projectDocuments,
    loading: projectDocumentsLoading,
    error: projectDocumentsError
  } = useProjectDocumentOptions({
    customerId: workflowData.customerId,
    projectId: workflowData.projectId,
    excludeIds,
    enabled: isOpen
  });

  const selection = useRegenerateSelection({
    workflowData,
    additionalDocuments,
    chatHistory,
    projectDocuments
  });

  const {
    organizedDocuments,
    includeChatHistory,
    toggleDocument,
    toggleChatHistory,
    selectDocumentsByType,
    deselectDocumentsByType,
    getSelection,
    getSelectionSummary,
    hasValidSelection,
    hasChatHistory,
    resetSelection
  } = selection;

  const summary = getSelectionSummary();

  const handleRegenerate = async () => {
    if (!hasValidSelection) return;
    
    try {
      await onRegenerate(getSelection());
    } catch (error) {
      console.error('Regeneration failed:', error);
    }
  };

  const getWorkflowTitle = () => {
    switch (workflowData.workflowType) {
      case 'document_comparison':
        return 'Document Comparison';
      case 'renewal':
      case 'renewal_analysis':
        return 'Renewal Analysis';
      case 'year_over_year':
        return 'Year-Over-Year Analysis';
      case 'loss_run':
      case 'loss_run_analysis':
        return 'Loss Run Analysis';
      case 'policy_comparison':
        return 'Policy Comparison';
      case 'research_analysis':
        return 'Research Analysis';
      default:
        return 'Report';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Regenerate Report
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select content to include in the regenerated {getWorkflowTitle().toLowerCase()} report
              {workflowData.customerName && (
                <span className="ml-1">for {workflowData.customerName}</span>
              )}
            </p>
          </div>
          
          <button
            onClick={onClose}
            disabled={isRegenerating}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Workflow Info */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-lg">🔄</span>
              <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                Regeneration Process
              </h3>
            </div>
            <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
              This will create a new version of your {getWorkflowTitle().toLowerCase()} report using the same AI analysis workflow. 
              The regenerated report will use only the content you select below, allowing you to focus on specific documents or incorporate insights from your conversation.
            </p>
          </div>

          {/* Original Documents Section */}
          {organizedDocuments.original.length > 0 && (
            <DocumentSelectionSection
              title="Original Documents"
              description={`Documents from the initial ${getWorkflowTitle().toLowerCase()} report`}
              documents={organizedDocuments.original}
              onToggleDocument={toggleDocument}
              onSelectAll={() => selectDocumentsByType('original')}
              onDeselectAll={() => deselectDocumentsByType('original')}
              badgeColor="blue"
            />
          )}

          {/* Additional Documents Section */}
          {organizedDocuments.additional.length > 0 && (
            <DocumentSelectionSection
              title="Additional Documents"
              description="Documents added after the initial report was generated"
              documents={organizedDocuments.additional}
              onToggleDocument={toggleDocument}
              onSelectAll={() => selectDocumentsByType('additional')}
              onDeselectAll={() => deselectDocumentsByType('additional')}
              badgeColor="green"
            />
          )}

          {/* Other Project Documents Section */}
          {workflowData.customerId && (
            <ProjectDocumentPicker
              documents={organizedDocuments.project}
              onToggleDocument={toggleDocument}
              onSelectAll={() => selectDocumentsByType('project')}
              onDeselectAll={() => deselectDocumentsByType('project')}
              loading={projectDocumentsLoading}
              error={projectDocumentsError}
            />
          )}

          {/* Chat History Section */}
          {hasChatHistory && (
            <ChatSelectionSection
              chatHistory={chatHistory}
              selected={includeChatHistory}
              onToggle={toggleChatHistory}
              messageLimit={summary.chatMessageLimit}
            />
          )}

          {/* Selection Summary */}
          <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
              <span className="mr-2">📋</span>
              Regeneration Summary
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {/* Document Summary */}
              <div className="space-y-1">
                <p className="font-medium text-gray-700 dark:text-gray-300">Documents</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {summary.totalSelected} of {summary.totalAvailable} selected
                </p>
                {summary.originalSelected > 0 && (
                  <p className="text-blue-600 dark:text-blue-400 text-xs">
                    • {summary.originalSelected} original
                  </p>
                )}
                {summary.additionalSelected > 0 && (
                  <p className="text-green-600 dark:text-green-400 text-xs">
                    • {summary.additionalSelected} additional
                  </p>
                )}
                {summary.projectSelected > 0 && (
                  <p className="text-purple-600 dark:text-purple-400 text-xs">
                    • {summary.projectSelected} from other project documents
                  </p>
                )}
              </div>

              {/* Chat Summary */}
              <div className="space-y-1">
                <p className="font-medium text-gray-700 dark:text-gray-300">Conversation</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {summary.chatIncluded ? 'Included' : 'Not included'}
                </p>
                {summary.chatMessageCount > 0 && (
                  <p className="text-purple-600 dark:text-purple-400 text-xs">
                    {summary.chatMessageCount > summary.chatMessageLimit
                      ? `• last ${summary.chatMessageLimit} of ${summary.chatMessageCount} messages will be included`
                      : `• ${summary.chatMessageCount} messages available`}
                  </p>
                )}
              </div>

              {/* Workflow Summary */}
              <div className="space-y-1">
                <p className="font-medium text-gray-700 dark:text-gray-300">Analysis Type</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {getWorkflowTitle()}
                </p>
                <p className="text-indigo-600 dark:text-indigo-400 text-xs">
                  • Same prompt as original
                </p>
              </div>
            </div>

            {/* Validation Messages */}
            {!hasValidSelection && (
              <div className="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded-lg">
                <div className="flex items-center space-x-2">
                  <span className="text-orange-600 dark:text-orange-400">⚠️</span>
                  <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                    Please select at least one document or include chat history to regenerate the report.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50">
          <div className="flex items-center justify-between">
            
            {/* Reset Button */}
            <button
              onClick={resetSelection}
              disabled={isRegenerating}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50 transition-colors"
            >
              Reset to defaults
            </button>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                disabled={isRegenerating}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              
              <button
                onClick={handleRegenerate}
                disabled={!hasValidSelection || isRegenerating}
                className="px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {isRegenerating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Regenerating...</span>
                  </>
                ) : (
                  <>
                    <span>🔄</span>
                    <span>Regenerate Report</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectiveRegenerateModal;